/**
 * Move Impact Preview — Lightweight Heuristic (Frontend-only)
 *
 * Quick conflict / balance check for "what if I move this truck?" decisions.
 * Uses load counts and time-window overlap, NOT a full time-step simulation.
 * Full simulation lives on the backend in whatIfSimulator.ts.
 *
 * This preview is suitable for UI hints (badges, color-coding) but should
 * NOT be presented as a definitive outcome prediction.
 */

import type { Appointment } from '../types/appointments';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MoveImpactResult {
    /** Would the move create a new conflict on the target dock? */
    createsConflict: boolean;
    /** Conflicting carrier name if conflict exists */
    conflictWith?: string;
    /** Would the move resolve an existing conflict on the source dock? */
    resolvesConflict: boolean;
    /** Number of appointments on source dock after move (should decrease) */
    sourceDockLoadAfter: number;
    /** Number of appointments on target dock after move (will increase) */
    targetDockLoadAfter: number;
    /** Net utilization improvement: positive = better balance */
    utilizationDelta: number;
    /** Short human-readable summary */
    summary: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
    return aStart < bEnd && aEnd > bStart;
}

// ─── Engine ───────────────────────────────────────────────────────────────────

/**
 * Preview the impact of moving an appointment to a different dock.
 *
 * @param appointment   The appointment being moved
 * @param targetDockId  The dock being considered as the new assignment
 * @param todaySchedule All appointments for today
 * @param now           Current time (ms)
 */
export function computeMoveImpact(
    appointment: Appointment,
    targetDockId: string,
    todaySchedule: Appointment[],
): MoveImpactResult {
    const apptStart = new Date(appointment.startAt).getTime();
    const apptEnd = new Date(appointment.endAt).getTime();
    const sourceDockId = appointment.dockId || appointment.dock_id;

    // Active (non-cancelled, non-completed) appointments
    const active = todaySchedule.filter(a =>
        a.id !== appointment.id &&
        !['cancelled', 'completed'].includes(a.status)
    );

    // Check for conflicts on target dock
    const targetConflicts = active.filter(a => {
        const dId = a.dockId || a.dock_id;
        if (dId !== targetDockId) return false;
        const s = new Date(a.startAt).getTime();
        const e = new Date(a.endAt).getTime();
        if (isNaN(s) || isNaN(e)) return false;
        return overlaps(apptStart, apptEnd, s, e);
    });

    const createsConflict = targetConflicts.length > 0;
    const conflictWith = createsConflict ? (targetConflicts[0].carrierName || 'Unknown') : undefined;

    // Check if source dock had conflicts that this move resolves
    let resolvesConflict = false;
    if (sourceDockId) {
        const sourceAppts = active.filter(a => {
            const dId = a.dockId || a.dock_id;
            return dId === sourceDockId;
        });
        // Count how many of those overlap with the appointment being moved
        const sourceConflicts = sourceAppts.filter(a => {
            const s = new Date(a.startAt).getTime();
            const e = new Date(a.endAt).getTime();
            if (isNaN(s) || isNaN(e)) return false;
            return overlaps(apptStart, apptEnd, s, e);
        });
        resolvesConflict = sourceConflicts.length > 0;
    }

    // Dock load after move
    const sourceDockLoadAfter = sourceDockId
        ? active.filter(a => (a.dockId || a.dock_id) === sourceDockId).length
        : 0;
    const targetDockLoadAfter = active.filter(a => (a.dockId || a.dock_id) === targetDockId).length + 1;

    // Utilization delta (positive = better balance, i.e. load moved from busy to idle)
    const utilizationDelta = sourceDockLoadAfter > 0
        ? (sourceDockLoadAfter - targetDockLoadAfter + 1) // +1 because we're adding to target
        : 0;

    // Summary
    let summary: string;
    if (createsConflict) {
        summary = `Creates conflict with ${conflictWith}`;
    } else if (resolvesConflict && utilizationDelta > 0) {
        summary = 'Resolves conflict + better balance';
    } else if (resolvesConflict) {
        summary = 'Resolves existing conflict';
    } else if (utilizationDelta > 0) {
        summary = 'Improves dock balance';
    } else {
        summary = 'No impact change';
    }

    return {
        createsConflict,
        conflictWith,
        resolvesConflict,
        sourceDockLoadAfter,
        targetDockLoadAfter,
        utilizationDelta,
        summary,
    };
}
